import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TopBar from '../components/TopBar';
import PrimaryButton from '../components/PrimaryButton';
import RabbiOnboardingForm, {
  initialValues as rabbiFormInitialValues,
  type RabbiOnboardingFormValues,
} from '../components/RabbiOnboardingForm';

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      resolve(base64 ?? '');
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

const RegisterRabbiRequestPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formValues, setFormValues] = useState<RabbiOnboardingFormValues>(rabbiFormInitialValues);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (!formValues.firstNameHe?.trim() || !formValues.lastNameHe?.trim()) {
      setError(
        t('onboarding_form_name_hebrew')
          ? `${t('onboarding_form_name_hebrew')} – required`
          : 'Name in Hebrew is required',
      );
      return;
    }
    if (!formValues.email?.trim()) {
      setError(
        t('onboarding_form_email')
          ? `${t('onboarding_form_email')} – required`
          : 'Email is required',
      );
      return;
    }

    setLoading(true);
    try {
      // Map new form to legacy addRabbi payload for now (backend may be updated later)
      const nusachMap: Record<string, string> = {
        sephardic: 'sfarad',
        ashkenazi: 'ashkenaz',
        yamen: 'yamen',
        moroccan: 'moroccan',
        jerusalem_sephardic: 'jerusalem',
        other: 'ashkenaz',
      };
      const nusach =
        formValues.readingStyles?.[0] != null
          ? (nusachMap[formValues.readingStyles[0]] ?? 'ashkenaz')
          : 'ashkenaz';

      const rabbiKey = formValues.email
        .trim()
        .replace(/[^a-zA-Z0-9]/g, '_')
        .slice(0, 32);

      const payload: Record<string, unknown> = {
        nusach,
        firstName: formValues.firstNameEn?.trim() || formValues.firstNameHe?.trim(),
        lastName: formValues.lastNameEn?.trim() || formValues.lastNameHe?.trim(),
        nameEn:
          [formValues.firstNameEn, formValues.lastNameEn].filter(Boolean).join(' ').trim() ||
          formValues.email,
        nameHe: [formValues.firstNameHe, formValues.lastNameHe].filter(Boolean).join(' ').trim(),
        titleHe: formValues.aboutYourself?.trim() || '',
        rating: '4.9',
        key: rabbiKey,
        experienceYears: Number(formValues.experienceYears) || 0,
        status: 'pending',
      };

      if (formValues.profileImageFile) {
        const base64 = await fileToBase64(formValues.profileImageFile);
        payload.profileImageBase64 = base64;
        payload.profileImageContentType = formValues.profileImageFile.type || 'image/jpeg';
      }

      const { FIREBASE_FUNCTIONS, callCloudFunction } = await import('../utils/firebaseFunctions');
      const response = await callCloudFunction('addRabbi', FIREBASE_FUNCTIONS.addRabbi(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok || data.success !== true) {
        throw new Error(data.error || 'Failed to submit request');
      }

      setSuccessMessage(
        data.rabbiId
          ? (t('rabbi_register_success') ?? `Request submitted. ID: ${data.rabbiId}`)
          : (t('rabbi_register_success_short') ?? 'Request submitted.'),
      );
      setFormValues(rabbiFormInitialValues);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to submit request';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white" style={{ color: 'var(--kol-navy, #132A49)' }}>
      <TopBar
        title={t('rabbi_register_link') ?? 'Register as rabbi'}
        onBack={() => navigate('/onboarding')}
      />
      <div
        style={{
          flex: 1,
          padding: '16px 20px 28px',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          alignItems: 'center',
          gap: 16,
          width: '100%',
        }}
      >
        <RabbiOnboardingForm value={formValues} onChange={setFormValues} />

        {error && (
          <div
            style={{
              width: '100%',
              maxWidth: 'min(400px, 90vw)',
              padding: 12,
              borderRadius: 10,
              border: '1px solid rgba(239, 68, 68, 0.4)',
              background: 'rgba(239, 68, 68, 0.08)',
              color: '#B91C1C',
            }}
          >
            {error}
          </div>
        )}
        {successMessage && (
          <div
            style={{
              width: '100%',
              maxWidth: 'min(400px, 90vw)',
              padding: 12,
              borderRadius: 10,
              border: '1px solid rgba(34, 197, 94, 0.4)',
              background: 'rgba(34, 197, 94, 0.08)',
              color: '#166534',
            }}
          >
            {successMessage}
          </div>
        )}

        <div
          style={{
            width: '100%',
            maxWidth: 'min(400px, 90vw)',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <PrimaryButton
            onClick={() => void handleSubmit()}
            text={loading ? (t('saving') ?? 'Saving...') : (t('submit') ?? 'Submit')}
            variant="gold"
            disabled={loading}
          />
          <PrimaryButton
            onClick={() => (successMessage ? navigate('/onboarding') : navigate(-1))}
            text={t('back') ?? 'Back'}
            variant="white"
          />
        </div>
      </div>
    </div>
  );
};

export default RegisterRabbiRequestPage;
